import { DropdownError } from "app/core/components/alerts/DropdownError"
import { FC, useMemo } from "react"
import { Props as SelectProps } from "react-select"
import { DropdownSelect } from "../input/DropdownSelect"
import { useEnhancedField } from "./DisabledContext"
import { FieldLabel } from "./LabeledTextField"

export interface LabeledDropdownSelectFieldProps extends SelectProps {
  /** Field name. */
  name: string
  /** Field label. */
  label: string
  creatable?: boolean
  onChange?: (value: any) => void
  overrideWrapperClassName?: string
  defaultValue?: SelectProps["value"]
  hideError?: boolean
  hideDropdownIndicator?: boolean
}

export const DropdownSelectField: FC<LabeledDropdownSelectFieldProps> = ({ ...allProps }) => {
  const {
    onChange: onChangeParent,
    name,
    label,
    options,
    defaultValue,
    overrideWrapperClassName,
    hideError = false,
    hideDropdownIndicator,
    ...props
  } = allProps
  const {
    input,
    meta: { touched, error, submitError, submitting },
    extra: { isDisabled },
    ...rest
  } = useEnhancedField(name)

  const normalizedError = Array.isArray(error) ? error.join(", ") : error || submitError
  const { onChange, value, ...restOfInput } = input

  const flattenedOptions = useMemo(() => {
    if (!options) {
      return undefined
    } else {
      return options.reduce((flattenedOptions, newOptions) => {
        if (newOptions.hasOwnProperty("value")) {
          return [...flattenedOptions, newOptions]
        } else {
          //@ts-ignore
          return [...flattenedOptions, ...newOptions.options]
        }
      }, [])
    }
  }, [options])

  return (
    <div className={overrideWrapperClassName ? overrideWrapperClassName : `relative mb-3`}>
      <FieldLabel htmlFor={name}>{label}</FieldLabel>
      <DropdownSelect
        aria-label={label}
        isDisabled={isDisabled}
        options={options}
        {...restOfInput}
        {...props}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            e.stopPropagation()
          }
        }}
        name={name}
        onChange={(e) => {
          if (e) {
            onChange(e.value)
            onChangeParent ? onChangeParent(e.value) : ""
          }
        }}
        // @ts-ignore
        value={
          defaultValue
            ? flattenedOptions?.find((v) => v.value === value) || defaultValue
            : flattenedOptions?.find((v) => v.value === value)
        }
        hideDropdownIndicator={hideDropdownIndicator}
        styles={{
          control: (provided, { selectProps: { width } }) => {
            return {
              ...provided,
              alignItems: "center",
              borderRadius: "0.375rem",
              borderStyle: "solid",
              borderWidth: "1px",
              borderColor: "rgba(255, 255, 255, 0.1)",
              boxShadow: undefined,
              boxSizing: "border-box",
              cursor: "pointer",
              display: "flex",
              backgroundColor: "rgba(151 151 151,0.10)",
              ":focus-within": { borderColor: "rgba(255, 255, 255, 0.25)" },
              ":hover": { borderColor: "rgba(255, 255, 255, 0.25)" },
              justifyContent: "space-between",
              label: "control",
              minHeight: "42px",
              outline: "0 !important",
              position: "relative",
              transition: "all 100ms",
              width: width,
            }
          },
        }}
        selectClassName="appearance-none shadow-none  focus:outline-0 focus:border-solid focus:border-opacity-25 bg-grayscale-lightest bg-opacity-10  w-full text-white"
      />
      {touched && normalizedError && !hideError && (
        <DropdownError hasLabel={label === "" ? false : true} normalizedError={normalizedError} />
      )}
    </div>
  )
}

export const DropdownSelectFieldWithNullOptions: FC<
  LabeledDropdownSelectFieldProps & {
    nullOptions?:
      | {
          label: string
          value: string
        }[]
      | null
  }
> = ({ nullOptions, ...allProps }) => {
  const dropdownNullOption = (() => {
    switch (typeof nullOptions) {
      case "undefined":
        return [
          {
            value: null as unknown as string, // Hack to get the options to line up
            label: "(None)",
          },
        ]
      case "object":
        return nullOptions === null ? [] : nullOptions
    }
  })()
  return <DropdownSelectField {...allProps} />
}
